import mapValues from 'lodash/mapValues';

export const makeColumnConfig = (definition) =>
	mapValues(definition, (column, key) => {
		const sourceField = column.sourceField || key;

		return {
			...column,
			getter: column.getter || ((row) => row[sourceField]),
			formatter: column.formatter || ((value) => value),
		};
	});

/**
 * Creates a column-header translation resolver from i18next's t function.
 *
 * Common usage in this app:
 *   const columnT = makeColumnTranslation(t, 'orders:columns');
 *   columnT('Order_Due_Date');
 *   -> t('orders:columns*Order_Due_Date', { keySeparator: '*' })
 *
 * It converts a column config key into a translation lookup key in the form:
 *   <namespaceKey>*<columnKey>
 *
 * Default example:
 *   makeColumnTranslation(t)('Order_Due_Date')
 *   -> t('columns*Order_Due_Date', { keySeparator: '*' })
 *
 * `keySeparator` is an advanced escape hatch. In normal usage, do not pass it.
 * The default (`*`) prevents i18next from treating periods as nested paths.
 *
 * @param {(key: string, options?: Record<string, unknown>) => string} t i18next translator function.
 * @param {string} [namespaceKey='columns'] Prefix namespace for column keys.
 * @param {string} [keySeparator='*'] Advanced: custom separator between namespace and key.
 * @returns {(key: string, options?: Record<string, unknown>) => string} Function that resolves a column key to translated header text.
 */
export const makeColumnTranslation =
	(t, namespaceKey = 'columns', keySeparator = '*') =>
	(key, options) =>
		t(`${namespaceKey.replace(/\./g, keySeparator)}${keySeparator}${key}`, {
			keySeparator,
			...options,
		});
